import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IoCloudDownloadOutline,
  IoDesktopOutline,
  IoExtensionPuzzleOutline,
  IoLinkOutline,
  IoOpenOutline,
  IoSettingsOutline,
  IoSparklesOutline,
} from "react-icons/io5";
import { CHROME_EXTENSION_URL, DESKTOP_APP_LANDING_URL } from "../../core/config";
import { useAuth } from "../../core/context/AuthContext";
import { signOut } from "../../core/services/auth";
import {
  ACTION_ITEMS_LIST_PATH,
  ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS,
  buildActionItemsSettingsPath,
  isBusinessSettingsRoute,
  parseActionItemsSettingsTab,
  pickActionItemsListFilterSearch,
} from "./actionItemHelpers";
import { getBusinessUserInitials } from "./shared/businessUserDisplay";
import { WhatsNewContext, WhatsNewProvider, useWhatsNew } from "./WhatsNewContext";
import { useCaptureHotkeyState } from "./shared/useCaptureHotkeyState";
import { RELEASES, latestWhatsNewVersion } from "./shared/whatsNew";
import WhatsNewPane from "./WhatsNewPane";
import logOutIcon from "../log-out.svg";

const ACCOUNT_POPOVER_WIDTH = 220;
const ACCOUNT_POPOVER_GAP = 6;
const HOVER_CLOSE_DELAY_MS = 140;

/**
 * Fixed position for the portaled account menu (opens above the footer trigger).
 *
 * @example
 * computeAccountPopoverPosition(anchorEl, false, popoverEl);
 * // => { top: 612, left: 14 } — 220px-wide menu above the account row
 */
const computeAccountPopoverPosition = (anchorEl, collapsed, popoverEl) => {
  const rect = anchorEl.getBoundingClientRect();
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;
  const popoverHeight = popoverEl?.offsetHeight || 168;

  if (collapsed) {
    let left = rect.right + ACCOUNT_POPOVER_GAP;
    let top = rect.top + rect.height / 2 - popoverHeight / 2;
    if (left + ACCOUNT_POPOVER_WIDTH > viewportW - 8) {
      left = rect.left - ACCOUNT_POPOVER_WIDTH - ACCOUNT_POPOVER_GAP;
    }
    top = Math.min(Math.max(8, top), viewportH - popoverHeight - 8);
    return { top, left };
  }

  let left = rect.left;
  let top = rect.top - popoverHeight - ACCOUNT_POPOVER_GAP;
  if (top < 8) {
    top = rect.bottom + ACCOUNT_POPOVER_GAP;
  }
  if (left + ACCOUNT_POPOVER_WIDTH > viewportW - 8) {
    left = Math.max(8, viewportW - ACCOUNT_POPOVER_WIDTH - 8);
  }
  return { top, left };
};

/**
 * Sidebar account row: hover opens menu with settings, What's new (with the latest version and a
 * purple dot while something is unopened), desktop app/extension install links, and sign out.
 *
 * @example
 * <BusinessSidebarUserMenu userName="Alex" userEmail="alex@co.com" collapsed={false} />
 */
const BusinessSidebarUserMenuContent = ({
  userName = "",
  userEmail = "",
  collapsed = false,
}) => {
  const { setUser, setIsAuthenticated } = useAuth();
  const { meet, releases, unseenIds, hasUnseen, markSeen } = useWhatsNew();
  const [paneOpen, setPaneOpen] = useState(false);
  // Entries that were unopened when the pane opened keep their dot while it stays open.
  const [freshIds, setFreshIds] = useState([]);
  const capture = useCaptureHotkeyState();
  const latestVersion = latestWhatsNewVersion(RELEASES);
  const isDesktopApp = Boolean(window.starmeshDesktop?.isElectron);
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState(null);
  const menuRootRef = useRef(null);
  const anchorRef = useRef(null);
  const popoverRef = useRef(null);
  const closeTimerRef = useRef(null);
  const initials = getBusinessUserInitials(userName);
  const displayName = String(userName || "").trim() || "Account";
  const settingsTab = parseActionItemsSettingsTab(location.search);
  const onSettingsRoute = isBusinessSettingsRoute(location.pathname);
  const isGeneralSettingsActive =
    onSettingsRoute && settingsTab !== ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS;
  const isConnectAppsActive =
    onSettingsRoute && settingsTab === ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS;

  const updatePopoverPosition = useCallback(() => {
    if (!anchorRef.current) return;
    setPopoverStyle(
      computeAccountPopoverPosition(anchorRef.current, collapsed, popoverRef.current)
    );
  }, [collapsed]);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openMenu = useCallback(() => {
    clearCloseTimer();
    setMenuOpen(true);
  }, [clearCloseTimer]);

  const scheduleCloseMenu = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setMenuOpen(false), HOVER_CLOSE_DELAY_MS);
  }, [clearCloseTimer]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    updatePopoverPosition();
    const handleReposition = () => updatePopoverPosition();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [menuOpen, updatePopoverPosition]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const frame = window.requestAnimationFrame(() => updatePopoverPosition());
    return () => window.cancelAnimationFrame(frame);
  }, [menuOpen, updatePopoverPosition]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleEscape = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
  }, [collapsed]);

  useEffect(
    () => () => {
      clearCloseTimer();
    },
    [clearCloseTimer]
  );

  const closeMenu = () => {
    clearCloseTimer();
    setMenuOpen(false);
  };

  const handleLogout = () => {
    closeMenu();
    setUser(null);
    setIsAuthenticated(false);
    signOut();
  };

  const handleCheckForUpdates = () => {
    closeMenu();
    void window.starmeshDesktop?.checkForUpdates?.();
  };

  const handleOpenSettings = () => {
    closeMenu();
    // Carry the task-list filter so closing settings restores it. Only the list
    // page's own query counts; elsewhere the search belongs to another screen.
    const onTaskList = location.pathname === ACTION_ITEMS_LIST_PATH;
    navigate(
      "/business/settings",
      onTaskList
        ? { state: { listSearch: pickActionItemsListFilterSearch(location.search) } }
        : undefined
    );
  };

  const handleOpenWhatsNew = () => {
    closeMenu();
    setFreshIds(unseenIds);
    // Opening it counts as seeing it: the dot clears, the entries stay listed.
    markSeen(unseenIds);
    setPaneOpen(true);
  };

  const closeWhatsNew = useCallback(() => setPaneOpen(false), []);

  const handleOpenConnectApps = () => {
    closeMenu();
    navigate(buildActionItemsSettingsPath({ tab: ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS }));
  };

  /** Straight to the Google Meet connector's own settings (where "Use Gemini notes" lives). */
  const handleOpenMeetSettings = () => {
    closeMenu();
    navigate(
      buildActionItemsSettingsPath({
        tab: ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS,
        workflow: meet.meetWorkflowId,
      })
    );
  };

  const itemClass = (active = false) =>
    `business-sidebar-account-popover__item${
      active ? " business-sidebar-account-popover__item--active" : ""
    }`;

  const renderMenuButton = ({ label, icon, onClick, active = false, title, trailing = null }) => (
    <button
      key={label}
      type="button"
      className={itemClass(active)}
      role="menuitem"
      onClick={onClick}
      title={title}
    >
      <span className="business-sidebar-account-popover__item-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="business-sidebar-account-popover__item-label">{label}</span>
      {trailing}
    </button>
  );

  const renderMenuLink = ({ label, icon, href, title }) => (
    <a
      key={label}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={itemClass()}
      role="menuitem"
      title={title}
      onClick={closeMenu}
    >
      <span className="business-sidebar-account-popover__item-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="business-sidebar-account-popover__item-label">{label}</span>
      <IoOpenOutline
        className="business-sidebar-account-popover__item-external"
        aria-hidden="true"
      />
    </a>
  );

  const accountPopover =
    menuOpen
      ? createPortal(
          <div
            ref={popoverRef}
            className="business-sidebar-account-popover"
            role="menu"
            aria-label="Account menu"
            onMouseEnter={openMenu}
            onMouseLeave={scheduleCloseMenu}
            style={{
              position: "fixed",
              top: popoverStyle?.top ?? -9999,
              left: popoverStyle?.left ?? -9999,
              visibility: popoverStyle ? "visible" : "hidden",
            }}
          >
            {renderMenuButton({
              label: "Settings",
              icon: <IoSettingsOutline aria-hidden="true" />,
              onClick: handleOpenSettings,
              active: isGeneralSettingsActive,
            })}
            {renderMenuButton({
              label: "Connectors",
              icon: <IoLinkOutline aria-hidden="true" />,
              onClick: handleOpenConnectApps,
              active: isConnectAppsActive,
            })}
            {renderMenuButton({
              label: "What's new",
              icon: <IoSparklesOutline aria-hidden="true" />,
              onClick: handleOpenWhatsNew,
              active: paneOpen,
              trailing: (
                <>
                  {latestVersion ? (
                    <span className="business-sidebar-account-popover__item-meta">v{latestVersion}</span>
                  ) : null}
                  {hasUnseen ? (
                    <span className="whats-new-dot whats-new-dot--menu" aria-label="New updates" />
                  ) : null}
                </>
              ),
            })}
            <div
              className="business-sidebar-account-popover__divider"
              role="separator"
              aria-hidden="true"
            />
            {renderMenuLink({
              label: "Install extension",
              icon: <IoExtensionPuzzleOutline aria-hidden="true" />,
              href: CHROME_EXTENSION_URL,
              title: "Select text on any website to create action items",
            })}
            {!isDesktopApp
              ? renderMenuLink({
                  label: "Download desktop app",
                  icon: <IoDesktopOutline aria-hidden="true" />,
                  href: DESKTOP_APP_LANDING_URL,
                  title: "Get the Task Mantis desktop app",
                })
              : renderMenuButton({
                  label: "Check for updates",
                  icon: <IoCloudDownloadOutline aria-hidden="true" />,
                  onClick: handleCheckForUpdates,
                  title: "Download the latest desktop installer if one is available",
                })}
            <div
              className="business-sidebar-account-popover__divider"
              role="separator"
              aria-hidden="true"
            />
            {renderMenuButton({
              label: "Sign out",
              icon: (
                <img
                  className="business-sidebar-account-popover__item-icon-image"
                  src={logOutIcon}
                  alt=""
                />
              ),
              onClick: handleLogout,
            })}
          </div>,
          document.body
        )
      : null;

  return (
    <div
      className="business-sidebar-user-menu"
      ref={menuRootRef}
      onMouseEnter={openMenu}
      onMouseLeave={scheduleCloseMenu}
      onFocus={openMenu}
      onBlur={(event) => {
        if (menuRootRef.current?.contains(event.relatedTarget)) return;
        if (popoverRef.current?.contains(event.relatedTarget)) return;
        scheduleCloseMenu();
      }}
    >
      <button
        type="button"
        ref={anchorRef}
        className={`business-sidebar-user-row${menuOpen ? " business-sidebar-user-row--open" : ""}`}
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        title={collapsed ? displayName : undefined}
      >
        <span className="business-sidebar-avatar" aria-hidden="true">
          {initials}
          {hasUnseen ? <span className="whats-new-dot whats-new-dot--avatar" /> : null}
        </span>
        <span className="business-sidebar-user-text">
          <span className="business-sidebar-user-name">{displayName}</span>
          {userEmail ? (
            <span className="business-sidebar-user-email">{userEmail}</span>
          ) : null}
        </span>
      </button>
      {accountPopover}
      <WhatsNewPane
        open={paneOpen}
        releases={releases}
        freshIds={freshIds}
        onClose={closeWhatsNew}
        meet={meet}
        capture={capture}
        isDesktopApp={isDesktopApp}
        desktopAppUrl={DESKTOP_APP_LANDING_URL}
        onOpenSettings={() => {
          setPaneOpen(false);
          handleOpenSettings();
        }}
        onOpenMeetSettings={() => {
          setPaneOpen(false);
          handleOpenMeetSettings();
        }}
      />
    </div>
  );
};

/** Shares the layout's What's new state when there is one; stands alone otherwise. */
const BusinessSidebarUserMenu = (props) =>
  useContext(WhatsNewContext) ? (
    <BusinessSidebarUserMenuContent {...props} />
  ) : (
    <WhatsNewProvider>
      <BusinessSidebarUserMenuContent {...props} />
    </WhatsNewProvider>
  );

export default BusinessSidebarUserMenu;
